import React, {FunctionComponent, useCallback, useState, useEffect} from "react";
import {useDropzone} from "react-dropzone";
import {useSnackbar} from "notistack";
import {ReactComponent as CameraBackGround} from "../../Images/camera.svg";
import {ReactComponent as Cross} from "../../Images/cross.svg";
import {Card, CardMedia, CircularProgress, Box} from "@material-ui/core";
import {makeStyles} from "@material-ui/core/styles";
import {useSelector} from "react-redux";
import {RootState} from "../../redux/store";
import heic2any from "heic2any";

interface Props {
    onLoad?: any,
    deletePhoto?: any,
    photo?: any,
    type?: string,
    sizeLimit?: boolean,
}

const useStyles = makeStyles({
    dropZoneContainer: {
        position: "relative",
        width: 133,
        height: 133,
        background: "#FFFFF",
        border: "1px solid #CCCDE5",
        boxSizing: "border-box",
        borderRadius: 6,
        overflow: "hidden",
        cursor: "pointer"
    },
    cameraBackground: {
        position: "absolute",
        left: 40,
        top: 40
    },
    crossContainer: {
        cursor: "pointer",
        zIndex: 11,
        backgroundColor: "#fff",
        borderRadius: 50,
        height: "16px",
        width: "16px",
        position: "absolute",
        right: 5,
        top: 5,
        textAlign: "center",
    },
    cross: {
        cursor: "pointer",
        zIndex: 12,
    },
    videoBtnMob: {
        color: "#000",
        width: "2em",
        height: "2em",
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        fontSize: "2em"
    },
    loader: {
        width: "100%",
        height: 133,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    }
}
);

const isHeicFile = (file: File) => {
    const mime = (file.type || "").toLowerCase();
    const name = file.name.toLowerCase();
    return mime === "image/heic" || mime === "image/heif" || name.endsWith(".heic") || name.endsWith(".heif");
};

const isAcceptedImage = (file: File) =>
    file.type === "image/jpeg" || file.type === "image/png" || isHeicFile(file);

export const DropZone: FunctionComponent<Props> = ({onLoad, photo, type = "img", deletePhoto, sizeLimit = true}: Props) => {
    const classes = useStyles();
    const [img, setImage] = useState(null);
    const [file, setFile] = useState(null);
    const [isConverting, setIsConverting] = useState(false);
    const {enqueueSnackbar} = useSnackbar();
    const isWhiteLabel = useSelector((state: RootState) => state?.branding.partnerPreferencesOn);
    const logo = useSelector((state: RootState) => state?.branding.logo);
    useEffect(() => {
        if (file) {
            setImage(file);
        }
        else {
            setImage(null);
            setFile(null);
        }
    }, []);

    useEffect(() => {
        if (photo) {
            setImage(photo);
        }
        else {
            setImage(null);
            setFile(null);
        }
    }, [photo]);
    const onDrop = useCallback(async (acceptedFiles: File[]) => {
        for (const droppedFile of acceptedFiles) {
            const setPreviewFromBlob = (blob: Blob) => {
                const reader = new FileReader();
                reader.onload = () => {
                    onLoad(reader.result);
                    setFile(reader.result);
                    setIsConverting(false);
                };
                reader.readAsDataURL(blob);
            };

            if (type === "img" && isAcceptedImage(droppedFile)) {
                if (sizeLimit && droppedFile.size >= 5000000) {
                    enqueueSnackbar(
                        "The selected image exceeds the size limit. Please select a different image or reduce this image's size",
                        {variant: "error", autoHideDuration: 3000}
                    );
                    continue;
                }

                if (isHeicFile(droppedFile)) {
                    setIsConverting(true);
                    try {
                        const converted = await heic2any({
                            blob: droppedFile,
                            toType: "image/jpeg",
                            quality: 0.92,
                        });
                        const jpegBlob = Array.isArray(converted) ? converted[0] : converted;
                        setPreviewFromBlob(jpegBlob);
                    } catch (error) {
                        console.error(error);
                        setIsConverting(false);
                        enqueueSnackbar(
                            "Could not process HEIC image. Please try a different format.",
                            {variant: "error", autoHideDuration: 3000}
                        );
                    }
                    continue;
                }

                setPreviewFromBlob(droppedFile);
                continue;
            }

            if (type === "video" && (droppedFile.type === "video/mp4" || droppedFile.type === "video/quicktime")) {
                setPreviewFromBlob(droppedFile);
                continue;
            }

            enqueueSnackbar(
                `Please upload ${type === "img" ? "JPG, PNG or HEIC" : "mp4/mov video"} format`,
                {variant: "error", autoHideDuration: 3000}
            );
        }
    }, [type, sizeLimit, onLoad, enqueueSnackbar]);

    const {getRootProps, getInputProps} = useDropzone({onDrop, disabled: isConverting});
    return (
        <div className={classes.dropZoneContainer}>
            <div {...getRootProps()} style={{zIndex: 10, width: 133, height: 133, position: "absolute"}}>
                <input {...getInputProps()} />
            </div>
            {photo && !isConverting && <div className={classes.crossContainer} onClick={deletePhoto}>
                <Cross className={classes.cross}/>
            </div>}
            {
                isConverting ?
                    <Box className={classes.loader}>
                        <CircularProgress size={32}/>
                    </Box>
                    : file || photo ?
                    <Card>
                        <CardMedia
                            // @ts-ignore
                            component={(file?.substr(4, 20).includes("video") || photo?.substr(4, 20).includes("video")) ? "img" : type}
                            alt="Contemplative Reptile"
                            height="130"
                            src={img}
                            image={
                                (file?.substr(4, 20).includes("video") || photo?.substr(4, 20).includes("video")) ?
                                    (( !isWhiteLabel && (file?.substr(4, 20).includes("video") || photo?.substr(4, 20).includes("video")))
                                        ? require("../../../src/Images/CircleLogo.png")
                                        : logo)
                                    : null}
                            title="Contemplative Reptile"
                        />
                    </Card> : <CameraBackGround className={classes.cameraBackground}/>
            }
        </div>
    );
};
